/** Fair pay status buckets used by More filters chips. */
export const FAIR_PAY_STATUS_KEYS = new Set([
  "below",
  "fair",
  "above",
  "none",
  "unfair",
]);

export const FAIR_PAY_STATUS_LABEL = {
  below: "Below fair",
  fair: "Fairly paid",
  above: "Above fair",
  none: "No fair pay status",
  unfair: "Unfair",
};

export const PAY_PLAN_FILTER_KEYS = new Set([
  "current",
  "none",
  "upcoming",
  "past",
  "has_current",
  "does_not_have_current",
]);

export const PAY_PLAN_FILTER_LABEL = {
  current: "Has current pay plan",
  none: "No current pay plan",
  upcoming: "Upcoming pay plan",
  past: "Past pay plan",
  has_current: "Has Current",
  does_not_have_current: "Does Not Have Current",
};

export const CHANGES_FILTER_KEYS = new Set([
  "none",
  "proposal",
  "evaluation",
  "rejected",
  "no_pending_changes",
  "has_evaluation",
  "has_proposal",
  "has_rejected_proposal",
  "has_upcoming",
  "not_eligible",
  "eligible",
]);

export const CHANGES_FILTER_LABEL = {
  none: "No changes",
  proposal: "Proposal",
  evaluation: "Evaluation",
  rejected: "Rejected proposal",
  no_pending_changes: "No Pending Changes",
  has_evaluation: "Has Evaluation",
  has_proposal: "Has Proposal",
  has_rejected_proposal: "Has Rejected Proposal",
  has_upcoming: "Has Upcoming",
  not_eligible: "Not Eligible",
  eligible: "Eligible",
};

export const PAY_PLAN_TOKEN_PREFIX = "pay_plan:";
export const CHANGES_TOKEN_PREFIX = "changes:";
export const DEPT_TOKEN_PREFIX = "dept:";

export const FAIR_PAY_STATUS_FILTER_GROUP = {
  label: "Fair Pay Status",
  items: [
    { label: "No Filter", value: null },
    { label: FAIR_PAY_STATUS_LABEL.below, value: "below" },
    { label: FAIR_PAY_STATUS_LABEL.fair, value: "fair" },
    { label: FAIR_PAY_STATUS_LABEL.above, value: "above" },
    { label: FAIR_PAY_STATUS_LABEL.none, value: "none" },
  ],
};

export const PAY_PLAN_FILTER_GROUP = {
  label: "Pay Plans",
  items: [
    { label: "No Filter", value: null },
    { label: PAY_PLAN_FILTER_LABEL.current, value: "current" },
    { label: PAY_PLAN_FILTER_LABEL.none, value: "none" },
    { label: PAY_PLAN_FILTER_LABEL.upcoming, value: "upcoming" },
    { label: PAY_PLAN_FILTER_LABEL.past, value: "past" },
  ],
};

export const CHANGES_FILTER_GROUP = {
  label: "Changes",
  items: [
    { label: "No Filter", value: null },
    { label: CHANGES_FILTER_LABEL.none, value: "none" },
    { label: CHANGES_FILTER_LABEL.proposal, value: "proposal" },
    { label: CHANGES_FILTER_LABEL.evaluation, value: "evaluation" },
    { label: CHANGES_FILTER_LABEL.rejected, value: "rejected" },
  ],
};

export const PAY_PLANS_LIST_SEARCH_PARAM_KEYS = ["employee_name", "primary_position_name"];

function invertLabelMap(map) {
  return Object.fromEntries(Object.entries(map).map(([key, label]) => [label, key]));
}

const FAIR_PAY_STATUS_BY_LABEL = invertLabelMap(FAIR_PAY_STATUS_LABEL);
const PAY_PLAN_BY_LABEL = invertLabelMap(PAY_PLAN_FILTER_LABEL);
const CHANGES_BY_LABEL = invertLabelMap(CHANGES_FILTER_LABEL);

export function fairPayStatusKeyFromLabel(label) {
  return FAIR_PAY_STATUS_BY_LABEL[label] || null;
}

export function payPlanKeyFromLabel(label) {
  return PAY_PLAN_BY_LABEL[label] || null;
}

export function changesKeyFromLabel(label) {
  return CHANGES_BY_LABEL[label] || null;
}

export function departmentFilterGroup(departments) {
  const items = [{ label: "No Filter", value: null }];
  (departments || []).forEach((dept) => {
    if (!dept?.name) return;
    items.push({
      label: dept.name,
      value: dept.id != null ? String(dept.id) : dept.name,
    });
  });
  return { label: "Department", items };
}

const BELOW_STATUSES = new Set(["below", "below-min"]);
const FAIR_STATUSES = new Set(["exact", "near-exact"]);
const ABOVE_STATUSES = new Set(["above", "above-max"]);

export function getFairPayStatusCategory(status) {
  if (!status) return "none";
  if (BELOW_STATUSES.has(status)) return "below";
  if (FAIR_STATUSES.has(status)) return "fair";
  if (ABOVE_STATUSES.has(status)) return "above";
  return "none";
}

export function getEmployeeSearchName(row) {
  const employee = row?.employee || {};
  return `${employee.firstName || ""} ${employee.lastName || ""}`.trim();
}

/**
 * Field-scoped OR search: row matches when any chip filter matches its field.
 * Mirrors Organization > Positions multi-field search behavior.
 */
export function rowMatchesSearchFilters(row, searchFilters) {
  const filters = Array.isArray(searchFilters) ? searchFilters : [];
  if (filters.length === 0) return true;

  const employeeName = getEmployeeSearchName(row).toLowerCase();
  const positionName = String(row?.employee?.positionTitle || "").toLowerCase();

  return filters.some((filter) => {
    const value = String(filter?.value || "")
      .trim()
      .toLowerCase();
    if (!value) return false;

    if (filter.fieldKey === "employee_name") {
      return employeeName.includes(value);
    }
    if (filter.fieldKey === "primary_position_name") {
      return positionName.includes(value);
    }
    return false;
  });
}

/**
 * Applies More-filters chips then multi-field search filters.
 */
export function getFilteredPayPlanRows(rows, filterChips, searchFilters) {
  const source = Array.isArray(rows) ? rows : [];
  const chips = Array.isArray(filterChips) ? filterChips : [];

  const departmentLabels = chips
    .filter((chip) => String(chip.key || "").startsWith("dept:"))
    .map((chip) => chip.label);

  const fairPayStatusKeys = chips
    .filter((chip) => FAIR_PAY_STATUS_KEYS.has(chip.key))
    .map((chip) => chip.key);

  let result = [...source];

  if (departmentLabels.length > 0) {
    result = result.filter((row) => departmentLabels.includes(row.department));
  }

  if (fairPayStatusKeys.length > 0) {
    result = result.filter((row) =>
      fairPayStatusKeys.includes(getFairPayStatusCategory(row.fairPayStatus?.status))
    );
  }

  return result.filter((row) => rowMatchesSearchFilters(row, searchFilters));
}

function pushChip(next, seen, key, label) {
  if (!key || seen.has(key)) return;
  seen.add(key);
  next.push({ key, label });
}

export function chipsFromAppliedPayPlanFilters(filters) {
  const next = [];
  const seen = new Set();

  (filters || []).forEach((raw) => {
    if (raw && typeof raw === "object" && raw.key && raw.label) {
      pushChip(next, seen, raw.key, raw.label);
      return;
    }

    const value = typeof raw === "string" ? raw : raw && raw.value != null ? raw.value : null;
    if (value == null || value === "" || value === "No Filter") return;

    const asString = String(value);
    const lower = asString.toLowerCase();
    const labelType = raw && typeof raw === "object" ? raw.labelType : null;

    if (asString.startsWith(PAY_PLAN_TOKEN_PREFIX)) {
      const key = asString.slice(PAY_PLAN_TOKEN_PREFIX.length);
      if (PAY_PLAN_FILTER_KEYS.has(key)) {
        pushChip(next, seen, asString, PAY_PLAN_FILTER_LABEL[key]);
      }
      return;
    }

    if (asString.startsWith(CHANGES_TOKEN_PREFIX)) {
      const key = asString.slice(CHANGES_TOKEN_PREFIX.length);
      if (CHANGES_FILTER_KEYS.has(key)) {
        pushChip(next, seen, asString, CHANGES_FILTER_LABEL[key]);
      }
      return;
    }

    if (asString.startsWith(DEPT_TOKEN_PREFIX)) {
      pushChip(next, seen, asString, asString.slice(DEPT_TOKEN_PREFIX.length));
      return;
    }

    if (labelType === "Fair Pay Status" || FAIR_PAY_STATUS_KEYS.has(lower) || FAIR_PAY_STATUS_BY_LABEL[asString]) {
      const statusKey = FAIR_PAY_STATUS_KEYS.has(lower) ? lower : FAIR_PAY_STATUS_BY_LABEL[asString];
      if (statusKey) {
        pushChip(next, seen, statusKey, FAIR_PAY_STATUS_LABEL[statusKey]);
      }
      return;
    }

    if (labelType === "Pay Plans" || PAY_PLAN_BY_LABEL[asString]) {
      const key = PAY_PLAN_BY_LABEL[asString] || lower;
      if (PAY_PLAN_FILTER_KEYS.has(key)) {
        pushChip(next, seen, `${PAY_PLAN_TOKEN_PREFIX}${key}`, PAY_PLAN_FILTER_LABEL[key]);
      }
      return;
    }

    if (labelType === "Changes" || CHANGES_BY_LABEL[asString]) {
      const key = CHANGES_BY_LABEL[asString] || lower;
      if (CHANGES_FILTER_KEYS.has(key)) {
        pushChip(next, seen, `${CHANGES_TOKEN_PREFIX}${key}`, CHANGES_FILTER_LABEL[key]);
      }
      return;
    }

    const deptKey = `${DEPT_TOKEN_PREFIX}${asString}`;
    pushChip(next, seen, deptKey, asString);
  });

  return next;
}

export function splitPayPlanFilterChips(chips) {
  const result = {
    fairPayStatus: null,
    payPlan: null,
    changes: null,
    department: null,
  };

  (chips || []).forEach((chip) => {
    const key = String(chip?.key || "");
    if (FAIR_PAY_STATUS_KEYS.has(key)) {
      result.fairPayStatus = key;
      return;
    }
    if (key.startsWith(PAY_PLAN_TOKEN_PREFIX)) {
      result.payPlan = key.slice(PAY_PLAN_TOKEN_PREFIX.length);
      return;
    }
    if (key.startsWith(CHANGES_TOKEN_PREFIX)) {
      result.changes = key.slice(CHANGES_TOKEN_PREFIX.length);
      return;
    }
    if (key.startsWith(DEPT_TOKEN_PREFIX)) {
      result.department = key.slice(DEPT_TOKEN_PREFIX.length);
    }
  });

  return result;
}

export function persistTokensFromChips(chips) {
  return (chips || []).map((chip) => chip.key).filter(Boolean);
}

export function paginateRows(rows, page, pageSize) {
  const safePageSize = Math.max(1, Number(pageSize) || 1);
  const total = Array.isArray(rows) ? rows.length : 0;
  const pageCount = Math.max(1, Math.ceil(total / safePageSize) || 1);
  const safePage = Math.min(Math.max(1, Number(page) || 1), pageCount);
  const start = (safePage - 1) * safePageSize;

  return {
    page: safePage,
    pageCount,
    pageRows: (rows || []).slice(start, start + safePageSize),
  };
}
